// @flow

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
  log,
  isObjectEmpty,
  REGEX_PATTERNS,
  formatPhoneNumberForFrontend,
  formatPhoneNumberForBackend
} from '../../utils/jsUtils';
import { login } from '../../reduxStore/modules/user/actions';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import SPBackend from '../../services/SPBackend';
import { useUserAuth } from '../Auth/UserSession';
import StyledPhoneInput from '../SharedComponents/StyledPhoneInput';

import Logo from '../../assets/askneo-favicon.png';
import { createClasses } from '../../utils/stylesUtils';
import signupStyles from './Styles/Signup.Style';
import staffInvitationHomeStyles from './Styles/StaffInvitationHome.Style';

const StaffInvitationHome = () => {
  // modern styling setup
  const theme = useTheme();
  const combinedStyles = { ...signupStyles, ...staffInvitationHomeStyles };
  const classes = createClasses(combinedStyles, theme);

  // form setup - using AddStaffDialog pattern (uncontrolled components)
  const { register, handleSubmit, errors, setValue, watch, getValues, formState, reset } = useForm();

  // Manually register phone field since we're not using Controller
  useEffect(() => {
    register({ name: 'phone' }, { required: true });
  }, [register]);

  // Watch phone value for StyledPhoneInput
  const phone = watch('phone', '');

  // path params
  const location = useLocation();
  let { inviteToken } = useParams();

  // auth hooks
  const {
    email,
    setEmail,
    loggedIn
  } = useUserAuth();

  // Redux dispatch for direct login
  const dispatch = useDispatch();

  // default redirect after successful login
  const { from } = location.state || { from: { pathname: '/member' } };

  // state vars
  const [submitBtnLabel, setSubmitBtnLabel] = useState('Accept invite');
  const [invite, setInvite] = useState({});
  const [companyName, setCompanyName] = useState('');
  const [serverError, setServerError] = useState('');
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const acceptInvite = async (data) => {
    setRegistrationLoading(true);
    setServerError('');
    log('accepting invite for data: ', data);
    const phoneNumber = data.phone;
    delete data.phone;

    // Use conditional logic based on invitation channel
    const submissionData = {
      ...data,
      Email: invite.Channel === 'email' ? email : data.Email,           // Use invitation email for email invites
      Phone: formatPhoneNumberForBackend(
        invite.Channel === 'sms' ? phone : phoneNumber                  // Use invitation phone for SMS invites
      )
    };

    log('submitting data with conditional logic: ', submissionData);

    try {
      const response = await SPBackend.acceptStaffInvite(inviteToken, submissionData);
      setSubmitBtnLabel('Welcome!');
      log('[acceptStaffInvite] response: ', response);

      // Use direct Redux login to avoid timing issues
      const loginEmail = invite.Channel === 'email' ? email : data.Email;
      log('[acceptInvite] Attempting login with email:', loginEmail, 'password length:', data.Password?.length);

      const loginResult = await dispatch(login(loginEmail, data.Password));
      log('[acceptInvite] Login result:', loginResult);

      if (loginResult.success) {
        log('[acceptInvite] Login successful, user should be redirected');
        setRegistrationLoading(false);
      } else {
        log('[acceptInvite] Login failed:', loginResult.error);
        setServerError(loginResult.error || 'Authentication failed. Please try logging in manually.');
        setRegistrationLoading(false);
      }
    } catch (e) {
      log('[acceptStaffInvite] error: ', e);
      const { response } = e;
      if (typeof response !== 'undefined') {
        setServerError(response.data);
      } else {
        setServerError('Connection issue. Please try again or contact us if the issue persists.');
      }
      setRegistrationLoading(false);
    }
  };

  useEffect(() => {
    if (inviteToken) {
      setTimeout(() => {
        log('[getStaffInvite]...');
        SPBackend.getStaffInvite(inviteToken)
          .then((response) => {
            const { Invite, User, CompanyName } = response.data;
            log('[getStaffInvite] data: ', response.data);
            setInvite(Invite);
            // Use setTimeout to ensure fields are registered before setting values
            setTimeout(() => {
              // Set email/phone based on invitation channel and pre-fill form
              if (Invite.Channel === 'email') {
                setValue('Email', User.Email || '');
                setEmail(User.Email || '');
                log('[getStaffInvite] Email invite - set email:', User.Email);
              } else if (Invite.Channel === 'sms') {
                const phoneValue = User.Phone ? formatPhoneNumberForFrontend(User.Phone) : '';
                setValue('phone', phoneValue);
                log('[getStaffInvite] SMS invite - set phone:', User.Phone, phoneValue);
              }

              // Pre-fill name fields
              setValue('FirstName', User.FirstName || '');
              setValue('LastName', User.LastName || '');
            }, 0);

            setCompanyName(CompanyName);
          })
          .catch((e) => {
            log('[getStaffInvite] error: ', JSON.stringify(e), e);
            const { response } = e;
            if (typeof response !== 'undefined') {
              setServerError(response.data);
            } else {
              setServerError('Connection issue. Please try again or contact us if the issue persists.');
            }
          });
        }, 400);
    }
  }, [inviteToken, setValue]);

  return (
    <Box
      sx={classes.contentCtr}
    >
      <Helmet>
        <title>Neo | Invite Staff</title>
      </Helmet>
      {
        loggedIn ? <Redirect push
                            to={from}
                   /> : null
      }
      <Box
        sx={classes.headerLogo}
      >
        <a href="/">
          <img
            height={40}
            src={Logo}
            alt="Neo Logo"
          />
        </a>
      </Box>
      {
        isObjectEmpty(invite)
        ? serverError === ''
          ? <Box sx={classes.loadingText}>
              {'Loading your invitation...'}
            </Box>
          : <Box sx={{ ...classes.error, ...classes.loadingText }}>
              { serverError }
            </Box>
        : <Box sx={classes.formContainer}>
          <Box>
            <Typography
              sx={classes.formTitle}
              variant={'h4'}
            >
              {`Your invitation to join the team at ${companyName}`}
            </Typography>
            <Typography
              sx={classes.formTitle}
              variant={'body2'}
            >
              {'Let\'s setup your access.'}
            </Typography>
          </Box>

          <Box>
          {
            <form onSubmit={handleSubmit(acceptInvite)}>
              <FormControl
                sx={classes.inputForm}
                variant='standard'
              >
                <InputLabel htmlFor='FirstName'>First Name</InputLabel>
                <Input
                  id='FirstName'
                  inputRef={register({ required: true })}
                  name='FirstName'
                  type='text'
                />
              </FormControl>
              <Box sx={classes.error}>
                {errors.FirstName && 'First name is required.'}
              </Box>
              <FormControl
                sx={classes.inputForm}
                variant='standard'
              >
                <InputLabel htmlFor='LastName'>Last Name</InputLabel>
                <Input
                  id='LastName'
                  inputRef={register({ required: true })}
                  name='LastName'
                  type='text'
                />
              </FormControl>
              <Box sx={classes.error}>
                {errors.LastName && 'Last name is required.'}
              </Box>
              <FormControl
                sx={classes.inputPhoneForm}
                variant='standard'
              >
                <InputLabel
                  htmlFor='phone'
                  shrink={!!phone || invite.Channel === 'sms'}
                >
                  Phone
                </InputLabel>
                <StyledPhoneInput
                  defaultCountry='US'
                  disabled={invite.Channel === 'sms' ? true : false}
                  id='phone'
                  onChange={(val) => setValue('phone', val)}
                  placeholder='Phone number'
                  value={phone}
                />
              </FormControl>
              <Box sx={classes.error}>
                {errors.phone && 'Phone number is required.'}
              </Box>
              <FormControl
                sx={classes.inputForm}
                variant='standard'
              >
                <InputLabel htmlFor='Email'>Email</InputLabel>
                <Input
                  autoComplete='email'
                  disabled={invite.Channel === 'email' ? true : false}
                  id='Email'
                  inputRef={register({ required: true, pattern: REGEX_PATTERNS.EMAIL })}
                  name='Email'
                  onChange={(e) => setEmail(e.target.value)}
                  type='email'
                />
              </FormControl>
              <Box sx={classes.error}>
                {errors.Email && 'Valid Email is required.'}
              </Box>

    
              <FormControl
                sx={classes.inputForm}
                variant='standard'
              >
                <InputLabel htmlFor='Password'>Password</InputLabel>
                <Input
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={e => e.preventDefault()}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                  id='Password'
                  inputRef={register({ required: true, minLength: 6 })}
                  name='Password'
                  type={showPassword ? 'text' : 'password'}
                />
              </FormControl>
              <Box sx={classes.error}>
                {errors.Password && errors.Password.type === 'required' && 'Password is required.'}
                {errors.Password && errors.Password.type === 'minLength' && 'Password must be at least 6 digits or characters.'}
              </Box>

              <Button
                sx={classes.submitBtn}
                color='primary'
                disabled={registrationLoading || !isObjectEmpty(errors)}
                type='submit'
                variant='contained'
              >
                {submitBtnLabel}
              </Button>
              <Box sx={{...classes.error, ...classes.errorWithMargin}}>
                {errors.agreed && 'Agreement to Terms & Conditions is required.'}
                {serverError}
              </Box>

              <Box sx={classes.securityInfo}>
                {`For security reasons, each invite can only be accepted by the same ${invite.Channel === 'email' ? 'email' : 'phone number'} it was sent to.`}
                <br />
                {`To register with another ${invite.Channel === 'email' ? 'email' : 'phone number'}, ask your team admin to send a new invite.`}
              </Box>
              </form>
            }
          </Box>
        </Box>
      }
    </Box>
  );
};

export default StaffInvitationHome;